import { Component, OnDestroy, OnInit, EnvironmentInjector, createComponent, inject, signal } from '@angular/core';
import { AfterViewInit } from '@angular/core';
import { RouterLink, Router, ActivatedRoute } from '@angular/router';
import { DataService } from '../../services/data.service';
// import { FilterService } from '../../services/filter.service'; // Removed
import { MapPoint } from '../../models/map-point';
import { getCategoryLabel, getCategoryIcon } from '../../enums/category.enum';

// Usar el L global que se carga desde angular.json scripts (incluye markercluster)
declare const L: any;
import { Subscription } from 'rxjs';

import { CardComponent } from '../../shared/card/card.component';
import { MapPopupComponent } from '../../shared/map-popup/map-popup.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-map',
  imports: [CardComponent, TranslateModule],
  templateUrl: './map.component.html',
  styleUrl: './map.component.css'
})


export class MapComponent implements AfterViewInit, OnInit, OnDestroy {
  private map!: any;
  private markerClusterGroup!: any;
  private trailLayers: any[] = []; // Array to hold sendero polylines
  private transportLayer!: any;
  private labelsLayer!: any;

  private pointsSub?: Subscription;
  selectedCategories = new Set<string>();

  private injector = inject(EnvironmentInjector);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private translateService = inject(TranslateService);
  // private filterService = inject(FilterService);

  zoom: boolean = true;
  zoomText: string = '';
  toponymyVisible = signal(true);
  streetsVisible = signal(false);
  isMobileFiltersOpen = signal(false);
  mapPoints: MapPoint[] = [];
  senderoPoints: MapPoint[] = []; // Separate array for senderos
  categories: string[] = [];
  filteredMapPoints: MapPoint[] = [];
  visibleSidebarPoints: MapPoint[] = [];
  hasVisibleAlojamientos: boolean = false;

  // Coordenadas objetivo desde query params
  private targetLat: number | null = null;
  private targetLng: number | null = null;
  private targetZoom: number = 16;

  private defaultIcon = L.icon({
    iconUrl: 'assets/leaflet/point.png',
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32]
  });

  // Mapa de iconos por categoría (usando los códigos del enum)
  private categoryIcons: { [key: string]: any } = {
    alo: L.icon({
      iconUrl: 'assets/icons/alo.png',
      iconSize: [32, 32],
      iconAnchor: [16, 32],
      popupAnchor: [0, -32]
    }),
    arq: L.icon({
      iconUrl: 'assets/icons/arquitectura.png',
      iconSize: [32, 32],
      iconAnchor: [16, 32],
      popupAnchor: [0, -32]
    }),
    evento: L.icon({
      iconUrl: 'assets/icons/eventos.png',
      iconSize: [32, 32],
      iconAnchor: [16, 32],
      popupAnchor: [0, -32]
    }),
    natural: L.icon({
      iconUrl: 'assets/icons/naturaleza.png',
      iconSize: [32, 32],
      iconAnchor: [16, 32],
      popupAnchor: [0, -32]
    }),
    playa: L.icon({
      iconUrl: 'assets/icons/playas.png',
      iconSize: [32, 32],
      iconAnchor: [16, 32],
      popupAnchor: [0, -32]
    }),
    pueblo: L.icon({
      iconUrl: 'assets/icons/pueblos.png',
      iconSize: [32, 32],
      iconAnchor: [16, 32],
      popupAnchor: [0, -32]
    }),
    sendero: L.icon({
      iconUrl: 'assets/icons/senderos.png',
      iconSize: [32, 32],
      iconAnchor: [16, 32],
      popupAnchor: [0, -32]
    })
  };

  constructor(private dataService: DataService) {
    this.zoomText = this.translateService.instant('MAP.DISABLE_ZOOM');
  }


  ngOnInit(): void {
    // Listen to Query Params
    this.route.queryParams.subscribe(params => {
      const cats = params['category'];
      this.selectedCategories.clear();

      if (cats) {
        if (Array.isArray(cats)) {
          cats.forEach((c: string) => this.selectedCategories.add(c));
        } else {
          this.selectedCategories.add(cats);
        }
      } else {
        // If no params, clear filter (show all)
        this.selectedCategories.clear();
      }

      // If 'todos' somehow got here, treat as empty/all
      if (this.selectedCategories.has('todos')) {
        this.selectedCategories.delete('todos');
      }

      // Leer coordenadas de los query params
      const lat = parseFloat(params['latitud']);
      const lng = parseFloat(params['longitud']);
      const zoom = parseInt(params['zoom'], 10);

      if (!isNaN(lat) && !isNaN(lng)) {
        this.targetLat = lat;
        this.targetLng = lng;
        this.targetZoom = !isNaN(zoom) ? zoom : 16;

        // Si el mapa ya está inicializado, mover a la posición
        if (this.map) {
          this.map.setView([this.targetLat, this.targetLng], this.targetZoom);
        }
      }

      // Update map view
      this.filterBySelectedCategories();
    });

    this.pointsSub = this.dataService.getPoints().subscribe({
      next: (data: MapPoint[]) => {
        // Separar senderos del resto
        this.senderoPoints = data.filter(p => p.categoria === 'sendero');
        this.mapPoints = data.filter(p => p.categoria !== 'sendero');

        this.filteredMapPoints = [...this.mapPoints];
        this.visibleSidebarPoints = [...this.mapPoints];

        if (this.map) {
          this.addPoints();
          this.addTrails(); // Pintar senderos
        }
        this.categories = [...new Set(data.map(point => point.categoria))];
      },
      error: (err) => console.error('Error fetching points', err)
    });
  }

  ngOnDestroy(): void {
    this.pointsSub?.unsubscribe();

  }


  ngAfterViewInit() {
    this.initMap();
    this.centerMapPosition();
    this.setupMapEvents();
    if (this.mapPoints.length > 0) {
      this.addPoints();
    }
    if (this.senderoPoints.length > 0) {
      this.addTrails();
    }
  }


  private initMap() {
    const imageryUrl =
      'https://services.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}';

    const bounds = L.latLngBounds(
      L.latLng(28.52, -14.15),  // Esquina suroeste (subido el límite sur)
      L.latLng(28.80, -13.75)   // Esquina noreste
    );

    this.map = L.map('map', {
      minZoom: 12,
      maxZoom: 18,
      zoomControl: false,
      scrollWheelZoom: true,
      doubleClickZoom: false,
      maxBounds: bounds,
      maxBoundsViscosity: 1.0  // Evita completamente salir de los límites
    });

    // Base satélite
    L.tileLayer(imageryUrl, {
      // maxZoom: 19, // si algún día subes el maxZoom del mapa
      attribution: 'Tiles © Esri'
    }).addTo(this.map);

    // Capa de transporte (calles, carreteras) — desactivada por defecto
    const transportationUrl = 'https://services.arcgisonline.com/ArcGIS/rest/services/Reference/World_Transportation/MapServer/tile/{z}/{y}/{x}';
    this.transportLayer = L.tileLayer(transportationUrl, {
      zIndex: 999,
      className: 'labels-layer'
    });

    // Capa de referencia (toponimias) — activada por defecto
    const hybridUrl = 'https://services.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}';
    this.labelsLayer = L.tileLayer(hybridUrl, {
      zIndex: 1000,
      className: 'labels-layer'
    }).addTo(this.map);


  }



  private setupMapEvents(): void {
    this.map.on('moveend zoomend', () => {
      this.updateVisibleSidebarPoints();
    });
  }


  private updateVisibleSidebarPoints(): void {
    if (!this.map) return;

    const bounds = this.map.getBounds();
    this.visibleSidebarPoints = [];
    this.hasVisibleAlojamientos = false;

    // 1) Añadir puntos (markers) visibles en el viewport
    if (this.markerClusterGroup && this.filteredMapPoints.length) {
      this.markerClusterGroup.eachLayer((layer: any) => {
        if (layer instanceof L.Marker) {
          const latLng = layer.getLatLng();
          if (latLng && bounds.contains(latLng)) {
            const point = this.filteredMapPoints.find(p => {
              if (!p.puntos || p.puntos.length < 2) return false;

              // Since we filtered senderos, puntos should be number[].
              // Check to satisfy TS and runtime safety.
              if (Array.isArray(p.puntos[0])) return false;

              const pLat = p.puntos[1] as number;
              const pLon = p.puntos[0] as number;

              return pLat !== undefined && pLat !== null &&
                pLon !== undefined && pLon !== null &&
                Math.abs(pLat - latLng.lat) < 0.0001 &&
                Math.abs(pLon - latLng.lng) < 0.0001;
            });
            if (point && point.categoria !== 'alo' && !this.visibleSidebarPoints.some(vp => vp.OBJECTID === point.OBJECTID)) {
              this.visibleSidebarPoints.push(point);
            }
            // Marcar que hay alojamientos visibles en el viewport
            if (point && point.categoria === 'alo') {
              this.hasVisibleAlojamientos = true;
            }
          }
        }
      });
    }

    // 2) Añadir senderos visibles en el viewport al sidebar
    if (this.selectedCategories.size === 0 || this.selectedCategories.has('sendero')) {
      const visibleSenderos = this.senderoPoints.filter(s => {
        if (!s.puntos || s.puntos.length === 0) return false;
        if (Array.isArray(s.puntos[0])) {
          const coords = s.puntos as number[][];
          return coords.some(coord => {
            if (coord.length < 2) return false;
            return bounds.contains(L.latLng(coord[1], coord[0]));
          });
        }
        return false;
      });
      visibleSenderos.forEach(sendero => {
        if (!this.visibleSidebarPoints.some(vp => vp.OBJECTID === sendero.OBJECTID)) {
          this.visibleSidebarPoints.push(sendero);
        }
      });
    }

    // Ordenar todo junto
    this.visibleSidebarPoints = this.sortVisiblePoints(this.visibleSidebarPoints);
  }


  private sortVisiblePoints(mapPoint: MapPoint[]): MapPoint[] {
    return mapPoint.sort((a, b) => {
      return a.denominacion.localeCompare(b.denominacion, 'es', {
        sensitivity: 'base',
        numeric: true
      });
    });
  }


  selectedCategory(category: string): void {
    const newSet = new Set(this.selectedCategories);
    if (newSet.has(category)) {
      newSet.delete(category);
    } else {
      newSet.add(category);
    }

    // Update URL
    const catsArray = Array.from(newSet);
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { category: catsArray.length > 0 ? catsArray : null },
      queryParamsHandling: 'merge'
    });

    const el = document.getElementById(`category-${category}`) as HTMLElement;
    if (el) {
      el.classList.toggle('selected');
    }
  }

  private filterBySelectedCategories(): void {
    if (this.selectedCategories.size === 0) {
      this.filteredMapPoints = [...this.mapPoints];
    } else {
      this.filteredMapPoints = this.mapPoints.filter(point =>
        this.selectedCategories.has(point.categoria)
      );
    }
    this.addPoints();
    this.addTrails(); // También filtrar senderos
    this.updateVisibleSidebarPoints();
  }


  activateZoom() {
    this.zoom = !this.zoom;
    if (this.zoom) {
      this.map.scrollWheelZoom.enable();
      this.zoomText = this.translateService.instant('MAP.DISABLE_ZOOM');
    } else {
      this.map.scrollWheelZoom.disable();
      this.zoomText = this.translateService.instant('MAP.ACTIVATE_ZOOM');
    }
  }

  toggleToponymy() {
    this.toponymyVisible.update(v => !v);
    if (this.toponymyVisible()) {
      this.labelsLayer.addTo(this.map);
    } else {
      this.map.removeLayer(this.labelsLayer);
    }
  }

  toggleStreets() {
    this.streetsVisible.update(v => !v);
    if (this.streetsVisible()) {
      this.transportLayer.addTo(this.map);
    } else {
      this.map.removeLayer(this.transportLayer);
    }
  }

  centerMapPosition() {
    if (this.targetLat !== null && this.targetLng !== null) {
      // Usar coordenadas de los query params
      this.map.setView([this.targetLat, this.targetLng], this.targetZoom);

      // Limpiar coordenadas de la URL para que no interfieran con filtros
      this.targetLat = null;
      this.targetLng = null;
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { latitud: null, longitud: null, zoom: null },
        queryParamsHandling: 'merge'
      });
    } else {
      // Posición por defecto: La Oliva
      const laOlivaCoordinates: [number, number] = [28.67, -13.928];
      const zoomLevel = 12;
      this.map.setView(laOlivaCoordinates, zoomLevel);
    }
  }


  addPoints(): void {
    if (this.markerClusterGroup) {
      this.map.removeLayer(this.markerClusterGroup);
    }

    if (!this.filteredMapPoints.length) return;

    this.markerClusterGroup = L.markerClusterGroup();

    this.filteredMapPoints.forEach((point) => {
      if (!point.puntos || !Array.isArray(point.puntos) || point.puntos.length < 2) {
        console.warn('Invalid point structure:', point);
        return;
      }
      // Ensure it is not a path (although filtered)
      if (Array.isArray(point.puntos[0])) return;

      const lat = point.puntos[1] as number;
      const lon = point.puntos[0] as number;

      if (isNaN(lat) || isNaN(lon)) {
        console.warn('Invalid coordinates:', point);
        return;
      }

      // Obtener el icono según la categoría del punto
      const icon = this.categoryIcons[point.categoria] || this.defaultIcon;

      const marker = L.marker([lat, lon], {
        icon: icon,
      });
      // marker.bindPopup(`${point.nombre} - ${point.categoria}`);
      marker.bindPopup(() => this.createPopupContent(point));
      this.markerClusterGroup.addLayer(marker);
    });

    this.map.addLayer(this.markerClusterGroup);
  }


  /**
   * Añade los senderos como polylines al mapa
   */
  addTrails(): void {
    // Primero eliminar los senderos anteriores
    this.removeTrails();

    if (!this.map) return;

    // Si hay filtro activo y 'sendero' no está seleccionado, no pintar
    if (this.selectedCategories.size > 0 && !this.selectedCategories.has('sendero')) {
      return;
    }

    this.senderoPoints.forEach((sendero) => {
      if (!sendero.puntos || !Array.isArray(sendero.puntos) || sendero.puntos.length === 0) {
        console.warn('Sendero sin puntos válidos:', sendero);
        return;
      }

      // Los puntos del sendero son arrays de [lon, lat]
      // Leaflet necesita [lat, lon], así que invertimos
      const latLngs: [number, number][] = [];

      // Verificar si puntos es un array de arrays (coordenadas de línea)
      if (Array.isArray(sendero.puntos[0])) {
        (sendero.puntos as number[][]).forEach(coord => {
          if (coord.length >= 2) {
            latLngs.push([coord[1], coord[0]]); // [lat, lon]
          }
        });
      } else {
        // Si solo tiene un punto [lon, lat], no se puede dibujar una línea
        console.warn('Sendero con un solo punto, no se puede dibujar línea:', sendero);
        return;
      }

      if (latLngs.length < 2) {
        console.warn('Sendero con menos de 2 coordenadas válidas:', sendero);
        return;
      }

      // "Borde" negro (línea más ancha debajo)
      const borderPolyline = L.polyline(latLngs, {
        color: 'black',
        weight: 5, // Más ancho que la línea principal
        opacity: 0.8,
        lineJoin: 'round',
        lineCap: 'round'
      });

      // Línea principal amarilla encima
      const mainPolyline = L.polyline(latLngs, {
        color: '#fcc200',
        weight: 3,
        opacity: 1,
        lineJoin: 'round',
        lineCap: 'round'
      });

      // Etiqueta con el código (ahora denominacion)
      if (sendero.denominacion) {
        // Solo los 4 primeros caracteres
        const labelText = sendero.denominacion.substring(0, 4);
        mainPolyline.bindTooltip(labelText, {
          permanent: true,
          direction: 'center',
          className: 'trail-label',
          opacity: 1
        });
      }

      // Popup al hacer clic en el sendero (en ambas líneas para facilitar el clic)
      const popupContentFn = () => this.createPopupContent(sendero);
      borderPolyline.bindPopup(popupContentFn);
      mainPolyline.bindPopup(popupContentFn);

      // Añadir al mapa y guardar referencia (orden importa: border primero)
      borderPolyline.addTo(this.map);
      mainPolyline.addTo(this.map);

      this.trailLayers.push(borderPolyline);
      this.trailLayers.push(mainPolyline);
    });

  }


  /**
   * Elimina todos los senderos del mapa
   */
  private removeTrails(): void {
    this.trailLayers.forEach(layer => {
      this.map.removeLayer(layer);
    });
    this.trailLayers = [];
  }


  private createPopupContent(point: MapPoint): HTMLElement {
    const component = createComponent(MapPopupComponent, {
      environmentInjector: this.injector
    });

    component.setInput('denominacion', point.denominacion);
    component.setInput('categoria', point.categoria);
    component.setInput('OBJECTID', point.OBJECTID);
    if (point.categoria !== 'alo') {
      component.setInput('image', '/assets/images/cards/' + point.OBJECTID + '.jpg');

    } else {
      component.setInput('image', '/assets/images/cards/accommodation1.jpg');

    }

    component.changeDetectorRef.detectChanges();

    component.instance.navigate.subscribe(() => {
      if (point.categoria === 'alo') {
        this.router.navigate(['/alojamientos']);
      } else {
        this.router.navigate(['/ficha', point.OBJECTID]);
      }
    });

    return component.location.nativeElement;
  }

  getCategoryLabel(category: string): string {
    return getCategoryLabel(category);
  }

  getCategoryIcon(category: string): string {
    return getCategoryIcon(category);
  }

  toggleMobileFilters(): void {
    this.isMobileFiltersOpen.update(v => !v);
  }

  closeMobileFilters(): void {
    this.isMobileFiltersOpen.set(false);
  }
}